import React, { useCallback, useEffect, useMemo, useRef } from "react";
import uuid from "react-uuid";
import ReactResizeDetector from "react-resize-detector";
import { DataPoint, DataPointWithGraphic, draw as chartDraw } from "./draw";
import _ from "lodash";
import styles from "./HorizontalTwoLineLabel.module.scss";

const MIN_LABEL_WIDTH = 100;
const MIN_LABEL_IF_BEYOND_PERCENT = 0.25;
const MIN_LABEL_HEIGHT = 41;

const BAR_GAP = 10;

export const WATCH_CLASSNAME = styles.displayLabelToMeasure;
export const UPDATE_CLASSNAME = styles.labelElement;

export interface HorizontalTwoLineLabelProps {
    labels: string[];
    displayedLabels: (string | JSX.Element)[];
    dataPoints: DataPoint[];

    series: string[];
    seriesBackground: string[];

    maxValue?: number;
    queryHeightWatchingLabels?: () => { watching: HTMLElement[]; update: HTMLElement[] };
    queryWidthWatchingLabels?: () => { watching: HTMLElement[]; update: HTMLElement[] };
    onHover?: (data: DataPointWithGraphic, tooltip: HTMLElement) => void;
}

export const HorizontalTwoLineLabel: React.FC<HorizontalTwoLineLabelProps> = ({
    labels,
    displayedLabels,
    dataPoints,
    series,
    seriesBackground,
    maxValue,
    queryHeightWatchingLabels,
    queryWidthWatchingLabels,
    onHover
}) => {
    const ref = useRef<HTMLDivElement>();
    const drawComplete = useRef<boolean>(false);
    const visualRef = useRef<HTMLDivElement>();
    const _uuid = useRef(uuid());

    const _displayedLabels = useMemo(() => {
        return (
            displayedLabels ??
            labels.map(d => {
                return d;
            })
        );
    }, [labels, displayedLabels]);

    const draw = useCallback(() => {
        const current = ref.current;
        if (!current) return;
        if (drawComplete.current) return;

        const width = (current as Element)?.getBoundingClientRect()?.width ?? current.clientWidth;
        const height = (current as Element)?.getBoundingClientRect()?.height ?? current.clientHeight;

        const scrollY = window.scrollY ?? 0;
        const panelY = (current as Element)?.getBoundingClientRect()?.y ?? 0;
        const objectY0 = Math.max(scrollY + panelY, 0);
        const objectY1 = objectY0 + Math.max(window.innerHeight * 0.3, height);
        const screenY0 = scrollY;
        const screenY1 = scrollY + window.innerHeight ?? 0;

        if (!((objectY0 <= screenY1 && screenY0 <= objectY0) || (objectY1 <= screenY1 && screenY0 <= objectY1))) return;

        drawComplete.current = true;

        // get width, height to adjust label sizes
        const refLabelHeight = queryHeightWatchingLabels
            ? _.max(Array.from(queryHeightWatchingLabels()?.watching).map(element => element.clientHeight)) ?? 0
            : 0;

        const refLabelWidth = queryWidthWatchingLabels
            ? _.max(
                  Array.from(queryWidthWatchingLabels()?.watching).map(
                      element => element?.getBoundingClientRect()?.width ?? element.clientWidth + 1
                  )
              ) ?? 0
            : 0;

        const labelHeight = _.ceil(
            _.max([
                ...Array.from(current.querySelectorAll(`.${WATCH_CLASSNAME}`)).map(
                    element => element?.getBoundingClientRect()?.height ?? element.clientHeight + 1
                ),
                refLabelHeight,
                MIN_LABEL_HEIGHT
            ])
        );

        const labelWidth = _.ceil(
            _.max([
                ...Array.from(current.querySelectorAll(`.${WATCH_CLASSNAME}`)).map(
                    element => element?.getBoundingClientRect()?.width ?? element.clientWidth + 1
                ),
                refLabelWidth,
                _.min([MIN_LABEL_WIDTH, width * MIN_LABEL_IF_BEYOND_PERCENT])
            ])
        );

        // update height of labels
        [
            ...(queryHeightWatchingLabels ? Array.from(queryHeightWatchingLabels()?.update ?? []) : []),
            ...Array.from(current.querySelectorAll(`.${UPDATE_CLASSNAME}`) ?? [])
        ].forEach(element => {
            (element as HTMLDivElement).style.minHeight = `${labelHeight}px`;
        });

        // update width of labels
        [
            ...(queryWidthWatchingLabels ? Array.from(queryWidthWatchingLabels()?.update ?? []) : []),
            ...Array.from(current.querySelectorAll(`.${UPDATE_CLASSNAME}`) ?? [])
        ].forEach(element => {
            (element as HTMLDivElement).style.minWidth = `${labelWidth}px`;
        });

        // use d3 to draw
        chartDraw(
            _uuid.current,
            visualRef.current,
            labels,
            dataPoints,
            series,
            seriesBackground,
            labelHeight,
            BAR_GAP,
            maxValue,
            onHover
        );
    }, [
        labels,
        dataPoints,
        series,
        seriesBackground,
        maxValue,
        queryHeightWatchingLabels,
        queryWidthWatchingLabels,
        onHover
    ]);

    const onResize = useCallback(() => {
        // force drawing
        drawComplete.current = false;
        draw();
    }, [draw]);

    const onScroll = useCallback(() => {
        draw();
    }, [draw]);

    useEffect(() => {
        const newOnScroll = _.debounce(onScroll, 10);

        window.addEventListener("scroll", newOnScroll, {
            passive: true
        });

        return () => {
            window.removeEventListener("scroll", newOnScroll);
        };
    }, [onScroll]);

    useEffect(() => {
        draw();
    }, [draw]);

    return (
        <div className={styles.horizontalTwoLineLabel} ref={ref}>
            {_displayedLabels.map((label, index) => {
                return (
                    <div key={index} className={WATCH_CLASSNAME}>
                        {label}
                    </div>
                );
            })}

            <div className={styles.legend}></div>
            <div className={styles.graph}>
                <div className={styles.label}>
                    {_displayedLabels.map((label, index) => {
                        return (
                            <div key={index} className={UPDATE_CLASSNAME}>
                                {label}
                            </div>
                        );
                    })}
                </div>
                <div className={styles.visualContainer}>
                    <div className={styles.visual}>
                        <div ref={visualRef}></div>
                    </div>
                </div>
            </div>

            <ReactResizeDetector handleWidth={true} onResize={onResize} />
        </div>
    );
};
